import type { EffectiveSource, FunctionModuleView } from "../api/access";

/**
 * Client-side mirror of the backend's effective-function algorithm
 * (docs/api/sprint7-uloge-i-funkcije.md §3), used for the live preview on the
 * user-access screen and for the locked "comes along" rows of the role editor:
 *
 *   base      = ( ∪ role : role.allFunctions ? ALL : role.functionKeys ) ∪ grant
 *   effective = closure(base) − withDependents(deny)
 *
 * `closure` adds everything a held function needs (§1.1, `FunctionView.requires`);
 * `withDependents` removes, with a denied function, everything that needs it.
 * DENY always wins, including over `allFunctions`.
 */

/** Direct requirements per function key — what the catalogue publishes. */
export type RequiresMap = ReadonlyMap<string, readonly string[]>;

export type AccessRole = {
  id: string;
  name: string;
  allFunctions: boolean;
  functionKeys: string[];
};

export type EffectiveEntry = {
  key: string;
  granted: boolean;
  source: EffectiveSource;
  /** Roles that give the function (also filled for DENY: which roles would have given it; for IMPLIED: which give what brings it). */
  roleNames: string[];
  /** IMPLIED: the held functions that bring this one. DENY_IMPLIED: the denied functions it needs. Else empty. */
  via: string[];
};

export type EffectiveInput = {
  /** Every function key known to the catalogue, in display order. */
  allKeys: readonly string[];
  /** The roles the user is a member of (already resolved to full role rows). */
  roles: readonly AccessRole[];
  grantKeys: ReadonlySet<string> | readonly string[];
  denyKeys: ReadonlySet<string> | readonly string[];
  /** The dependency graph; without it every function stands alone (older callers, tests). */
  requires?: RequiresMap;
};

const NO_REQUIRES: RequiresMap = new Map();

function toSet(v: ReadonlySet<string> | readonly string[]): ReadonlySet<string> {
  return v instanceof Set ? v : new Set(v);
}

/** The dependency graph straight out of the catalogue (`GET /api/admin/functions`). */
export function requiresMap(modules: readonly FunctionModuleView[]): Map<string, string[]> {
  const out = new Map<string, string[]>();
  for (const m of modules) for (const f of m.functions) out.set(f.key, f.requires ?? []);
  return out;
}

function walk(keys: Iterable<string>, edges: (key: string) => readonly string[]): Set<string> {
  const seen = new Set<string>();
  const stack: string[] = [];
  for (const k of keys) if (!seen.has(k)) { seen.add(k); stack.push(k); }
  while (stack.length > 0) {
    const k = stack.pop() as string;
    for (const next of edges(k)) if (!seen.has(next)) { seen.add(next); stack.push(next); }
  }
  return seen;
}

/** The keys plus everything they (transitively) require — what holding them really means. */
export function closure(keys: Iterable<string>, requires: RequiresMap): Set<string> {
  return walk(keys, (k) => requires.get(k) ?? []);
}

/** The keys plus everything that (transitively) requires one of them — what denying them takes away. */
export function withDependents(keys: Iterable<string>, requires: RequiresMap): Set<string> {
  const requiredBy = new Map<string, string[]>();
  for (const [k, needs] of requires) {
    for (const n of needs) {
      const list = requiredBy.get(n);
      if (list) list.push(k); else requiredBy.set(n, [k]);
    }
  }
  return walk(keys, (k) => requiredBy.get(k) ?? []);
}

/** One entry per key of `allKeys`, in the same order. */
export function computeEffective(input: EffectiveInput): EffectiveEntry[] {
  const requires = input.requires ?? NO_REQUIRES;
  const grants = toSet(input.grantKeys);
  const denies = toSet(input.denyKeys);
  const byRole = input.roles.map((r) => ({ name: r.name, all: r.allFunctions, keys: new Set(r.functionKeys) }));
  const order = new Map(input.allKeys.map((k, i) => [k, i]));
  const inOrder = (keys: Iterable<string>) => [...keys].sort((a, b) => (order.get(a) ?? 0) - (order.get(b) ?? 0));

  const giversOf = (key: string) => byRole.filter((r) => r.all || r.keys.has(key)).map((r) => r.name);
  const base = new Set<string>(grants);
  for (const key of input.allKeys) if (giversOf(key).length > 0) base.add(key);
  const held = closure(base, requires);
  const deniedAll = withDependents(denies, requires);

  return input.allKeys.map((key) => {
    const roleNames = giversOf(key);
    if (denies.has(key)) return { key, granted: false, source: "DENY", roleNames, via: [] };
    if (deniedAll.has(key)) {
      // Needs something that was taken away — name the denied functions it depends on.
      const via = inOrder([...closure([key], requires)].filter((k) => k !== key && denies.has(k)));
      return { key, granted: false, source: "DENY_IMPLIED", roleNames, via };
    }
    if (roleNames.length > 0) return { key, granted: true, source: "ROLE", roleNames, via: [] };
    if (grants.has(key)) return { key, granted: true, source: "GRANT", roleNames, via: [] };
    if (held.has(key)) {
      // Nobody lists it, but a held function needs it — name the held functions that bring it.
      const via = inOrder([...base].filter((b) => b !== key && closure([b], requires).has(key)));
      const names: string[] = [];
      for (const b of via) for (const n of giversOf(b)) if (!names.includes(n)) names.push(n);
      return { key, granted: true, source: "IMPLIED", roleNames: names, via };
    }
    return { key, granted: false, source: "NONE", roleNames, via: [] };
  });
}

/** Convenience: only the keys the user effectively holds. */
export function effectiveKeys(input: EffectiveInput): Set<string> {
  return new Set(computeEffective(input).filter((e) => e.granted).map((e) => e.key));
}
